import { useState } from 'react';
import { Link } from 'react-router-dom';

function CaseStudiesPreview({ hideTitle = false, hideButton = false }) {
  const cases = [
    {
      company: 'BeautyPillow.com',
      results: {
        growth: '357%',
        timeframe: '3 months',
        markets: '5 MENAP countries'
      },
      quote: 'Before SellAbroad, we struggled with product localization and compliance in MENAP markets. Their platform automated our Arabic translations, handled regional certifications, and optimized our pricing across different marketplaces. Within three months, our sales grew by 357% and we expanded from UAE to five MENAP countries seamlessly.',
      images: [
        'https://i.ibb.co/Nx5QnxL/s-3024x-copy.jpg',
        'https://i.ibb.co/1qNCZgF/BP-Skin-6-1800x1800.webp',
        'https://i.ibb.co/7K2HcbF/BP-Skin-5-1800x1800.webp'
      ]
    },
    {
      company: 'Novimed.Org',
      results: {
        growth: '289%',
        timeframe: '4 months',
        markets: '8 MENAP countries'
      },
      quote: 'Managing inventory across multiple MENAP marketplaces was a nightmare until we found SellAbroad. Their unified dashboard and smart allocation system eliminated overselling and optimized our stock levels. They also handled all our warranty documentation and local customer support, which helped us achieve a 289% growth while maintaining a 98% satisfaction rate.',
      images: [
        'https://i.ibb.co/82xMFc1/NOV002-2-07abbaea-3e7d-4c61-8034-9d6593ae1833-1080x.webp',
        'https://i.ibb.co/HdC65RF/NOV002-4-1080x.webp',
        'https://i.ibb.co/9gDKx4K/NOV002-3-1080x.webp'
      ]
    }
  ];

  const [currentSlides, setCurrentSlides] = useState({});

  const nextSlide = (company) => {
    setCurrentSlides(prev => ({
      ...prev,
      [company]: ((prev[company] || 0) + 1) % 3
    }));
  };

  const prevSlide = (company) => {
    setCurrentSlides(prev => ({
      ...prev,
      [company]: ((prev[company] || 0) - 1 + 3) % 3
    }));
  };

  return (
    <section className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {!hideTitle && (
          <div className="text-center">
            <h2 className="text-base font-semibold text-[#0049ac] tracking-wide uppercase">Case Studies</h2>
            <p className="mt-2 text-3xl font-extrabold text-gray-900 sm:text-4xl lg:text-5xl">
              Success Stories from MENAP
            </p>
            <p className="mt-4 max-w-2xl text-xl text-gray-500 lg:mx-auto">
              See how leading brands are achieving remarkable growth with our platform
            </p>
          </div>
        )}

        <div className="mt-16 space-y-16">
          {cases.map((caseStudy, index) => (
            <div
              key={caseStudy.company}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-8 lg:gap-12`}
            >
              <div className="flex-1">
                <div className="space-y-6">
                  <h3 className="text-2xl font-bold text-gray-900">{caseStudy.company}</h3>
                  <div className="grid grid-cols-3 gap-4">
                    {Object.entries(caseStudy.results).map(([key, value]) => (
                      <div key={key} className="bg-white p-4 rounded-xl shadow-md hover:shadow-lg transition-all duration-300">
                        <p className="text-xl font-bold text-[#0049ac]">{value}</p>
                        <p className="text-sm text-gray-500 capitalize">{key}</p>
                      </div>
                    ))}
                  </div>
                  <blockquote className="text-lg text-gray-500">{caseStudy.quote}</blockquote>
                </div>
              </div>

              <div className="flex-1">
                <div className="relative h-64 lg:h-96 overflow-hidden rounded-2xl shadow-md group">
                  <img
                    className="w-full h-full object-contain bg-white transition-all duration-500 group-hover:scale-105"
                    src={caseStudy.images[currentSlides[caseStudy.company] || 0]}
                    alt={`${caseStudy.company} - Image ${(currentSlides[caseStudy.company] || 0) + 1}`}
                  />
                  <div className="absolute bottom-4 right-4 flex space-x-2">
                    <button
                      onClick={() => prevSlide(caseStudy.company)}
                      className="p-2 rounded-full bg-white/90 hover:bg-white shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <svg className="h-5 w-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                      </svg>
                    </button>
                    <button
                      onClick={() => nextSlide(caseStudy.company)}
                      className="p-2 rounded-full bg-white/90 hover:bg-white shadow-lg hover:shadow-xl transition-all duration-300"
                    >
                      <svg className="h-5 w-5 text-gray-800" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>
                  <div className="absolute bottom-4 left-4 flex space-x-2">
                    {[0, 1, 2].map((dot) => (
                      <button
                        key={dot}
                        onClick={() => setCurrentSlides(prev => ({ ...prev, [caseStudy.company]: dot }))}
                        className={`w-2 h-2 rounded-full transition-all duration-300 ${
                          (currentSlides[caseStudy.company] || 0) === dot
                            ? 'bg-[#0049ac] w-4'
                            : 'bg-gray-400'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {!hideButton && (
          <div className="mt-16 text-center">
            <Link
              to="/case-studies"
              className="inline-flex items-center px-8 py-4 border border-transparent text-lg font-medium rounded-full text-white bg-[#0049ac] hover:scale-105 hover:shadow-lg transform transition-all duration-300 font-poppins"
            >
              View All Case Studies
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}

export default CaseStudiesPreview;